/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from '@mui/material/ListItemIcon';
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import Divider from '@mui/material/Divider';
import { Cached, ExitToApp, SettingsOutlined, Description, Group, Sync, Add } from '@mui/icons-material';
import { NavLink } from "react-router-dom";
import "./styles.css";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { useObservable } from "src/framework/store/storeUtil";
import { BehaviorSubject } from "rxjs";
import jwtDecode from "jwt-decode";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import api, { STATUS_CODE } from "src/framework/providers/api";
import { IconButton, ListItemButton, Toolbar, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoginStore } from "src/framework/store/LoginStore";
import { notifierStore } from "src/framework/store/NotifierStore";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { pwaPromptEvent$ } from "src/store/global.store";

export interface UserInfo {
  name: string;
  email?: string;
  icon?: string | null;
  image?: string;
}

export interface UserParams {
  data: UserInfo;
  onChange: Function;
  element: HTMLButtonElement | null;
}

export const authUser$ = new BehaviorSubject<any>(null);

const MenuUser: FC<UserParams> = ({ data, element, onChange }) => {
  const authUser = useObservable(authUser$);
  const open = Boolean(element);
  const id = open ? 'simple-popover' : undefined;
  const avatarName = data.name.match(/(\b\S)?/g)?.join("")?.match(/(^\S|\S$)?/g)?.join("").toUpperCase();
  const permissoes: string[] = useObservable(LoginStore.permissoes) || [];
  const currentUser = useObservable(LoginStore.userInfoSubject())
  const pwaPromptEvent = useObservable(pwaPromptEvent$)

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const canShowColaboradoresItem = useMemo(()=>{
    return PermissionUtil.can(PermissionUtil.PERMISSION_LISTAGEM_COLABORADORES, permissoes);
  },[permissoes])

  const handleClose = (value: string) => {
    onChange(null);
  };

  const cleanCache = () => {
    document.location.reload();
  };

  const installPwa = useCallback(()=>{
    if (pwaPromptEvent) {
      pwaPromptEvent.prompt();
      pwaPromptEvent.userChoice.then((choiceResult: any) => {
        pwaPromptEvent$.next(null);
      });
    }
  },[pwaPromptEvent])

  const exibirSincronizacaoPagamentos = useMemo(()=>{
    const emailsPermitidos = (process.env.REACT_APP_EMAIL_USUARIOS_SINCRONIZACAO_PAGAMENTOS || "").split(";").filter(item => !!item);
    return emailsPermitidos.includes(currentUser?.email)
  },[currentUser])

  const sincronizarPagamentos = useCallback(()=>{
    confirmationDialog.setup({
      content: "Deseja sincronizar pagamentos ?",
      title: "Sincronizar pagamentos",
      onConfirmation: async () => {
        const syncResponse = await api.post({
          url: "/projetos/status/sincronizar"
        })

        if (syncResponse.statusCode === STATUS_CODE.OK) {
          confirmationDialog.close();
          
          notifierStore.setNotifier({
            content: ["Sincronização realizada com sucesso."],
            title: "Aviso",
            timeOut: 4000,
            onClose: () => {
              document.location.reload();
              notifierStore.setOpen(false)
            },
          });
          notifierStore.setOpen(true);
        } else {
          notifierStore.setNotifier({
            content: ["Erro ao sincronizar pagamentos."],
            title: "Aviso",
            timeOut: 4000,
            onClose: () => notifierStore.setOpen(false),
          });
          notifierStore.setOpen(true);
        }
      },
      onClose: () => confirmationDialog.close(),
    });
    confirmationDialog.open();
  },[])

  const onLogout = () => {  
    confirmationDialog.setup({
      content: "Deseja sair da plataforma?",
      title: "Sair",
      onConfirmation: async () => {
        const token = LocalStorage.get("solar.token")

        if (!token) {
          window.location.href = `${window.location.origin}/signout`
          return
        }

        let decoded: any = null

        try {
          decoded = jwtDecode(token)
        } catch (error) {
          window.location.href = `${window.location.origin}/signout`
          return
        }

        if (decoded?.type !== 'conta') {
          window.location.href = `${window.location.origin}/signout`
          return
        }

        const redirectURL = await LoginStore.doLogOutConta()
        if (redirectURL) window.location.href = redirectURL
  
      },
      onClose: () => confirmationDialog.close(),
    });
    confirmationDialog.open();
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={element}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      anchorReference={isMobile ? "none" : "anchorEl"}
      PaperProps={{
        style: isMobile
          ? {
              position: "fixed",
              top: 0,
              right: 0,
              width: "80vw",
              height: "100vh",
              minHeight: "100%",
              borderRadius: 0,
              boxShadow: theme.shadows[16],
              transition: "transform 0.3s ease-out",
              background: "#fff",
            }
          : {},
      }}
    >
      <Box>
        {isMobile && (
          <Toolbar
            style={{
              alignItems: "center",
              justifyContent: "flex-end",
              borderBottom: "1px solid #ddd",
              background: "#fff",
              height: 64, 
            }}
          >
            <IconButton edge="end" color="inherit" onClick={() => onChange(null)} size="large">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        )}

        <Box className="box">
          <Avatar>{avatarName}</Avatar>
          <Box className="box-info">
            <ListItemText className="title-info" primary={data?.name} />
            <ListItemText className="text-info" primary={data?.email} />
            <ListItemText className="text-info" primary={authUser?.role} />
          </Box>
        </Box>

        <Divider />
        {isMobile && <br />}

        <Box>
          {(PermissionUtil.can(PermissionUtil.PERMISSION_CODIGO_SERVICO_MANUTENCAO, permissoes) ||
            PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DISTRIBUIDOR_LISTAGEM, permissoes) ||
            PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_EMAIL_LISTAGEM, permissoes) ||
            PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_EMAIL_MANUTENCAO, permissoes) ||
            PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_CONTAS_PERMITE_TROCA_LOGO, permissoes) ||
            PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_PROPOSTA_COMERCIAL, permissoes)) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to="/projetos/contas/configuracoes"
                    onClick={() => onChange(null)}
                  >
                    <ListItemIcon>
                      <SettingsOutlined />
                    </ListItemIcon>
                    <ListItemText className="text-menu" primary="Configurações" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          {canShowColaboradoresItem && (
            <ListItem disablePadding>
              <ListItemButton
                component={NavLink}
                to="/colaboradores"
                onClick={() => onChange(null)}
              >
                <ListItemIcon>
                  <Group />
                </ListItemIcon>
                <ListItemText className="text-menu" primary="Colaboradores" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/termos_acesso"
              onClick={() => onChange(null)}
            >
              <ListItemIcon>
                <Description />
              </ListItemIcon>
              <ListItemText className="text-menu" primary="Termos" />
            </ListItemButton>
          </ListItem>

          {exibirSincronizacaoPagamentos && (
            <ListItem disablePadding>
              <ListItemButton onClick={sincronizarPagamentos}>
                <ListItemIcon>
                  <Sync />
                </ListItemIcon>
                <ListItemText className="text-menu" primary="Sincronizar pagamentos" />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding>
            <ListItemButton onClick={cleanCache}>
              <ListItemIcon>
                <Cached />
              </ListItemIcon>
              <ListItemText className="text-menu" primary="Limpar Cache" />
            </ListItemButton>
          </ListItem>

          {pwaPromptEvent && <ListItem disablePadding>
            <ListItemButton onClick={installPwa}>
              <ListItemIcon>
                <Add />
              </ListItemIcon>
              <ListItemText className="text-menu" primary="Adicionar à tela inicial" />
            </ListItemButton>
          </ListItem>}

          <ListItem disablePadding>
            <ListItemButton onClick={onLogout}>
              <ListItemIcon>
                <ExitToApp />
              </ListItemIcon>
              <ListItemText className="text-menu" primary="Sair" />
            </ListItemButton>
          </ListItem>
        </Box>
      </Box>
    </Popover>
  );
};

export default MenuUser;
